import { GTM_EVENT_TYPE, GTM_VARIABLE } from '../data-types/google-tag-manager/googleTagManagerEnums';
import { TrackerOptionalParameters } from '../data-types/trackers/trackersInterfaces';
import { getCurrencySymbol } from '../international/internationalCurrencies';
import { setIncomeAccessDepositTracking, setIncomeAccessRegistrationTracking } from '../state/actions/iframeActions';
import { setUserDetails } from '../state/actions/userDataActions';
import { store } from '../state/store';
import { trackEventGTM } from './googleTagManager';
import { tryToSetIncomeAccessDepositTrackingInfo } from './incomeAccessPixel';
import {
  trackSportradarDeposit,
  trackSportradarFinishRegistration,
  trackSportradarLogin,
  trackSportradarStartRegistration,
} from './sportradarPixel';
import {
  twitterPixelTrackDeposit,
  twitterPixelTrackFinishRegistration,
  twitterPixelTrackFirstDeposit,
  twitterPixelTrackLogin,
  twitterPixelTrackPageView,
  twitterPixelTrackStartRegistration,
} from './twitterPixel';

export const trackRegistrationStarted = (params: TrackerOptionalParameters) => {
  const { userId, phoneNumber, fingerprint } = params;
  const uniqueId = userId || phoneNumber || fingerprint;
  const timestamp = new Date().toISOString();

  trackSportradarStartRegistration();
  twitterPixelTrackStartRegistration(`${uniqueId}_${timestamp}`);
  trackEventGTM(GTM_EVENT_TYPE.STARTED_REGISTRATION, {
    [GTM_VARIABLE.EVENT_SCOPED_USER_ID]: userId ? String(userId) : undefined,
    [GTM_VARIABLE.FINGERPRINT]: fingerprint,
    [GTM_VARIABLE.TIMESTAMP]: timestamp,
  });
};

export const trackRegistrationFinished = (params: TrackerOptionalParameters) => {
  const { userId, fingerprint } = params;
  const timestamp = new Date().toISOString();

  if (userId) {
    trackSportradarFinishRegistration(userId);
    twitterPixelTrackFinishRegistration(`${userId}_${timestamp}`);
  }
  setIncomeAccessRegistrationTracking(true);

  trackEventGTM(GTM_EVENT_TYPE.COMPLETED_REGISTRATION, {
    [GTM_VARIABLE.EVENT_SCOPED_USER_ID]: userId ? String(userId) : undefined,
    [GTM_VARIABLE.FINGERPRINT]: fingerprint,
    [GTM_VARIABLE.TIMESTAMP]: timestamp,
  });
};

export const trackSessionLogin = (userId: number, userEmail: string) => {
  trackSportradarLogin(userId);
  twitterPixelTrackLogin(`${userId}_${new Date().toISOString()}`, userEmail || null);
  trackEventGTM(GTM_EVENT_TYPE.SESSION_LOGIN, {
    [GTM_VARIABLE.USER_SCOPED_USER_ID]: String(userId),
  });
};

export const trackLoginWithCredentials = () => {
  const {
    userData: { user },
  } = store.getState();

  const userId = user && user.id;
  const timestamp = new Date().toISOString();

  if (userId) {
    trackEventGTM(GTM_EVENT_TYPE.LOGIN_WITH_CREDENTIALS, {
      [GTM_VARIABLE.EVENT_SCOPED_USER_ID]: String(userId),
      [GTM_VARIABLE.TIMESTAMP]: timestamp,
    });
  }
};

export const trackUserLogout = (userId: number | undefined) => {
  trackEventGTM(GTM_EVENT_TYPE.LOGOUT, { [GTM_VARIABLE.USER_SCOPED_USER_ID]: userId ? String(userId) : undefined });
};

export const trackDeposit = (depositedAmount: number, transactionId: number | undefined) => {
  const {
    userData: { user, userLocation },
  } = store.getState();

  tryToSetIncomeAccessDepositTrackingInfo(depositedAmount, transactionId);

  if (user && typeof user.id === 'number') {
    const isUsersFirstDeposit = !user.firstDepositAt;
    const timestamp = new Date().toISOString();

    if (isUsersFirstDeposit) {
      setIncomeAccessDepositTracking(true);
      setUserDetails({ firstDepositAt: new Date().toString() });
    }

    trackSportradarDeposit(user.id, depositedAmount, getCurrencySymbol(user, userLocation), isUsersFirstDeposit);

    const preKeyPart = transactionId !== undefined ? `${transactionId}_` : '';
    const twitterUniqueKey = `${preKeyPart}${user.id}_${new Date().toISOString()}`;

    if (isUsersFirstDeposit) {
      twitterPixelTrackFirstDeposit(twitterUniqueKey, depositedAmount);
      trackEventGTM(GTM_EVENT_TYPE.FIRST_TIME_DEPOSIT, {
        [GTM_VARIABLE.EVENT_SCOPED_USER_ID]: String(user.id),
        [GTM_VARIABLE.TRANSACTION_ID]: transactionId ? String(transactionId) : '',
        [GTM_VARIABLE.TIMESTAMP]: timestamp,
      });
    } else {
      twitterPixelTrackDeposit(twitterUniqueKey, depositedAmount);
      trackEventGTM(GTM_EVENT_TYPE.DEPOSIT, {
        [GTM_VARIABLE.EVENT_SCOPED_USER_ID]: String(user.id),
        [GTM_VARIABLE.TRANSACTION_ID]: transactionId ? String(transactionId) : '',
        [GTM_VARIABLE.TIMESTAMP]: timestamp,
      });
    }
  }
};

export const trackZimplerPnPFirstDeposit = (depositedAmount: number) => {
  const {
    userData: { user, userLocation },
  } = store.getState();

  if (user && user.id && user.firstDepositAt) {
    const timestamp = new Date().toISOString();

    trackSportradarDeposit(user.id, depositedAmount, getCurrencySymbol(user, userLocation), true);

    const preKeyPart = '';
    const twitterUniqueKey = `${preKeyPart}${user.id}_${timestamp}`;
    twitterPixelTrackFirstDeposit(twitterUniqueKey, depositedAmount);

    trackEventGTM(GTM_EVENT_TYPE.FIRST_TIME_DEPOSIT, {
      [GTM_VARIABLE.EVENT_SCOPED_USER_ID]: String(user.id),
      [GTM_VARIABLE.TRANSACTION_ID]: '',
      [GTM_VARIABLE.TIMESTAMP]: timestamp,
    });
  }
};

export const trackPageView = (uniqueId: string) => {
  twitterPixelTrackPageView(`${uniqueId}_${new Date().toISOString()}`);
};
